import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, CalendarDays, ArrowDownCircle, Pencil, Trash2, History, Check } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtEUR, fmtEUR2, fmtDate, MONTHS_FR } from "@/lib/format";
import { CARD, Label, Loader, EmptyState, ProgressBar, ErrorRetry } from "@/components/common";
import { TxRow } from "@/pages/Mouvements";
import TransactionDialog from "@/components/TransactionDialog";
import DailyRevenueDialog from "@/components/DailyRevenueDialog";
import MarkReceivedDialog from "@/components/MarkReceivedDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RetroMonthCard } from "@/components/retro/RetroMonthCard";
import { ShowMore, JumpBar } from "@/components/ShowMore";
import { RecapReglements } from "@/components/RecapReglements";

const KIND_LABEL = { secu: "Sécu / CMU", mutuelle: "Mutuelle", alma: "Alma" };
const KIND_STYLE = { secu: "bg-sky-50 text-sky-700", mutuelle: "bg-violet-50 text-violet-700", alma: "bg-amber-50 text-amber-700" };
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const now = new Date();

export default function Encaissements() {
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [txns, setTxns] = useState([]);
  const [daily, setDaily] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [addTx, setAddTx] = useState(false);
  const [addDaily, setAddDaily] = useState(false);
  const [editingDaily, setEditingDaily] = useState(null);
  const [confirmDaily, setConfirmDaily] = useState(null);
  const [prats, setPrats] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterPrat, setFilterPrat] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [audit, setAudit] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [receiveTx, setReceiveTx] = useState(null);
  const [recSearch, setRecSearch] = useState("");
  const [allTx, setAllTx] = useState([]);
  const { user } = useAuth();
  const isOwner = user?.is_superadmin || ["owner", "admin"].includes(user?.role);

  const load = useCallback(async (silent) => {
    if (silent !== true) setLoading(true);
    setError(false);
    try {
      const [t, d, dash, p, rx] = await Promise.all([
        api.get(`/transactions?year=${ym.year}&month=${ym.month}&type=income`),
        api.get(`/daily-revenues`),
        api.get(`/dashboard?year=${ym.year}&month=${ym.month}`),
        api.get(`/practitioners`),
        api.get(`/transactions`),
      ]);
      setTxns(t.data); setDaily(d.data); setDash(dash.data); setPrats(p.data);
      const rec = (rx.data || [])
        .filter((x) => ["secu", "mutuelle", "alma"].includes(x.revenue_kind) && x.status === "planned")
        .sort((a, b) => (a.transaction_date < b.transaction_date ? -1 : 1));
      setReceivables(rec);
      setAllTx(rx.data || []);
      api.get(`/daily-revenues/audit`).then((r) => setAudit(r.data)).catch(() => {});
      api.get(`/sites`).then((r) => setSites(r.data)).catch(() => {});
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, [ym]);

  const markReceived = (r) => setReceiveTx(r);

  useEffect(() => { load(); }, [load]);
  const c = dash?.current;
  const pname = (id) => prats.find((p) => p.id === id)?.name || "Non attribué";
  const sname = (id) => sites.find((x) => x.id === id)?.name;
  const monthsAvail = Array.from(new Set(daily.map((d) => d.date.slice(0, 7)))).sort().reverse();
  const filteredDaily = daily.filter((d) =>
    (filterPrat === "all" || (filterPrat === "none" ? !d.practitioner_id : d.practitioner_id === filterPrat)) &&
    (filterMonth === "all" || d.date.slice(0, 7) === filterMonth));
  const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const shownRec = receivables.filter((r) => !recSearch.trim() || norm(`${r.patient_name} ${r.mutuelle_name} ${r.label}`).includes(norm(recSearch.trim())));
  const auditLabel = { create_daily: "Création", update_daily: "Modification", delete_daily: "Suppression" };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-400">Ce qui rentre</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Encaissements</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-daily-btn" variant="outline" onClick={() => { setEditingDaily(null); setAddDaily(true); }} className="rounded-full"><CalendarDays className="mr-1.5 h-4 w-4" />CA du jour</Button>
          <Button data-testid="add-income-btn" onClick={() => setAddTx(true)} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Plus className="mr-1.5 h-4 w-4" />Recette</Button>
        </div>
      </div>

      <div className="mt-4">
        <Select value={`${ym.year}-${ym.month}`} onValueChange={(v) => { const [y, m] = v.split("-"); setYm({ year: +y, month: +m }); }}>
          <SelectTrigger className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Array.from({ length: 15 }).map((_, i) => {
              const d = new Date(now.getFullYear(), now.getMonth() - 3 + i, 1);
              return <SelectItem key={i} value={`${d.getFullYear()}-${d.getMonth() + 1}`}>{MONTHS_FR[d.getMonth() + 1]} {d.getFullYear()}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      {loading ? <Loader /> : error || !c ? <ErrorRetry onRetry={load} /> : (
        <div className="mt-5 space-y-5">
          <JumpBar sections={[["enc-progress", "Progression"], ["enc-retro", "Rétrocessions"], ["enc-recettes", "Recettes du mois", txns.length], ["pending-transfers", "Virements en attente", receivables.length], ["enc-recap", "Récapitulatif"], ["enc-daily", "Journaliers", filteredDaily.length], ...(isOwner ? [["daily-audit-journal", "Journal"]] : [])]} />
          <div id="enc-progress" className={`${CARD} scroll-mt-32 md:scroll-mt-14 p-6`}>
            <div className="flex items-center justify-between">
              <Label>Progression des encaissements</Label>
              <span className="font-display text-xl font-bold text-emerald-600">{c.progression_encaissements}%</span>
            </div>
            <div className="mt-3"><ProgressBar value={c.progression_encaissements} color="emerald" height="h-4" /></div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center text-sm">
              <div><p className="text-xs text-gray-400">Déjà encaissé</p><p className="font-bold text-emerald-600">{fmtEUR(c.deja_encaisse)}</p></div>
              <div><p className="text-xs text-gray-400">Prévu total</p><p className="font-bold">{fmtEUR(c.entrees_prevues)}</p></div>
              <div><p className="text-xs text-gray-400">Reste à encaisser</p><p className="font-bold text-amber-600">{fmtEUR(c.reste_a_encaisser)}</p></div>
            </div>
          </div>

          <div id="enc-retro" className="scroll-mt-32 md:scroll-mt-14"><RetroMonthCard year={ym.year} month={ym.month} refreshKey={c} onPaid={() => load(true)} /></div>

          <div id="enc-recettes" className={`${CARD} scroll-mt-32 md:scroll-mt-14 p-4 sm:p-6`}>
            <Label className="mb-2">Recettes du mois ({txns.length})</Label>
            {txns.length === 0 ? <EmptyState title="Aucune recette" subtitle="Ajoutez votre première recette." action={<Button onClick={() => setAddTx(true)} className="rounded-full bg-[#065F46]">Ajouter</Button>} />
              : <ShowMore items={txns} testid="recettes" className="" render={(t) => <TxRow key={t.id} t={t} onChange={load} />} />}
          </div>

          <div id="pending-transfers" className={`${CARD} scroll-mt-32 md:scroll-mt-14 p-4 sm:p-6`} data-testid="pending-transfers">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <Label>Virements en attente ({receivables.length} · {fmtEUR2(receivables.reduce((s, r) => s + (r.amount || 0), 0))})</Label>
              {receivables.length > 0 && <Input data-testid="receivable-search" value={recSearch} onChange={(e) => setRecSearch(e.target.value)} placeholder="Rechercher un patient…" className="h-9 w-full rounded-full sm:w-64" />}
            </div>
            {receivables.length === 0 ? <p className="py-2 text-sm text-gray-400">Aucun virement en attente. Les montants Sécu / CMU (+7 j), Mutuelles (+14 j) et Alma (+3 j) apparaîtront ici jusqu'à leur réception.</p> : shownRec.length === 0 ? <p className="py-2 text-sm text-gray-400" data-testid="receivable-no-match">Aucun virement ne correspond à « {recSearch} ».</p> : (
              <ShowMore items={shownRec} testid="receivables" initial={recSearch.trim() ? 50 : 5} render={(r) => (
                  <div key={r.id} className="flex items-center gap-3 py-3" data-testid={`receivable-row-${r.id}`}>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${KIND_STYLE[r.revenue_kind]}`}>{KIND_LABEL[r.revenue_kind]}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{r.patient_name || r.label}{r.mutuelle_name ? <span className="font-normal text-gray-500"> · {r.mutuelle_name}</span> : null}</p>
                      <p className="text-xs text-gray-400">{r.patient_name ? `${r.label.split(" — ").pop()} · ` : ""}{pname(r.practitioner_id)} · virement prévu le {fmtDate(r.transaction_date)}</p>
                    </div>
                    <p className="font-display font-bold text-emerald-600">{fmtEUR2(r.amount)}</p>
                    <Button data-testid={`receive-${r.id}`} size="sm" onClick={() => markReceived(r)} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Check className="mr-1 h-3.5 w-3.5" />Marquer reçu</Button>
                  </div>
                )} />
            )}
          </div>

          <div id="enc-recap" className={`${CARD} scroll-mt-32 md:scroll-mt-14 p-4 sm:p-6`} data-testid="recap-reglements">
            <RecapReglements daily={daily} txs={allTx} pname={pname} sname={sname} prats={prats} sites={sites} />
          </div>

          <div id="enc-daily" className={`${CARD} scroll-mt-32 md:scroll-mt-14 p-4 sm:p-6`}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <Label>Encaissements journaliers ({filteredDaily.length})</Label>
              <div className="flex gap-2">
                <Select value={filterPrat} onValueChange={setFilterPrat}>
                  <SelectTrigger data-testid="filter-practitioner" className="w-40 rounded-full"><SelectValue placeholder="Praticien" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les praticiens</SelectItem>
                    <SelectItem value="none">Non attribué</SelectItem>
                    {prats.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterMonth} onValueChange={setFilterMonth}>
                  <SelectTrigger data-testid="filter-month" className="w-36 rounded-full"><SelectValue placeholder="Mois" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les mois</SelectItem>
                    {monthsAvail.map((m) => {
                      const [yy, mm] = m.split("-");
                      return <SelectItem key={m} value={m}>{MONTHS_FR[+mm]} {yy}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {filteredDaily.length === 0 ? <p className="py-4 text-sm text-gray-400">Aucun CA journalier pour ce filtre.</p> : (
              <ShowMore items={filteredDaily.slice(0, 200)} testid="daily" render={(d) => (
                  <div key={d.id} className="flex items-center gap-3 py-3" data-testid={`enc-daily-row-${d.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">{pname(d.practitioner_id)}{sname(d.site_id) && <span data-testid={`enc-daily-site-${d.id}`} className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-[#065F46]">{sname(d.site_id)}</span>}</p>
                      <p className="text-xs text-gray-400">{fmtDate(d.date)} · CB {fmtEUR(d.cb)} · Esp. {fmtEUR(d.especes)} · Chq {fmtEUR(d.cheque)} · Sécu/CMU {fmtEUR(d.secu)} · Mut. {fmtEUR(d.mutuelle || 0)}{d.alma ? ` · Alma ${fmtEUR(d.alma)}` : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display font-bold text-emerald-600">{fmtEUR2(d.total)}</p>
                      {d.logiciel > 0 && <p className={`text-xs ${Math.abs(d.ecart) < 0.01 ? "text-gray-400" : "text-amber-600"}`}>écart {fmtEUR2(d.ecart)}</p>}
                    </div>
                    <button data-testid={`edit-daily-${d.id}`} onClick={() => { setEditingDaily(d); setAddDaily(true); }} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100" aria-label="Modifier"><Pencil className="h-4 w-4" /></button>
                    {isOwner && <button data-testid={`delete-daily-${d.id}`} onClick={() => setConfirmDaily(d)} className="rounded-lg p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                )} />
            )}
          </div>

          {isOwner && (
            <div id="daily-audit-journal" className={`${CARD} scroll-mt-32 md:scroll-mt-14 p-4 sm:p-6`} data-testid="daily-audit-journal">
              <div className="mb-3 flex items-center gap-2 text-gray-400"><History className="h-4 w-4" /><Label>Journal des modifications (CA journaliers)</Label></div>
              {audit.length === 0 ? <p className="py-2 text-sm text-gray-400">Aucune modification enregistrée pour le moment.</p> : (
                <ShowMore items={audit.slice(0, 30)} testid="audit" render={(a) => (
                    <div key={a.id} className="py-2.5 text-sm" data-testid={`audit-row-${a.id}`}>
                      <p className="font-medium">
                        <span className={a.action === "delete_daily" ? "text-rose-600" : a.action === "update_daily" ? "text-amber-600" : "text-emerald-600"}>{auditLabel[a.action] || a.action}</span>
                        {" · "}{pname(a.meta?.practitioner_id)}{a.meta?.date ? ` · ${fmtDate(a.meta.date)}` : ""}
                      </p>
                      <p className="text-xs text-gray-400">
                        par {a.user_name || a.user} · {new Date(a.at).toLocaleString("fr-FR")}
                        {a.action === "update_daily" && a.meta?.old_total != null
                          ? ` · ${fmtEUR2(a.meta.old_total)} → ${fmtEUR2(a.meta.total)}`
                          : a.meta?.total != null ? ` · ${fmtEUR2(a.meta.total)}` : ""}
                      </p>
                    </div>
                  )} />
              )}
            </div>
          )}
        </div>
      )}

      <TransactionDialog open={addTx} onOpenChange={setAddTx} type="income" onSaved={load} />
      <DailyRevenueDialog open={addDaily} onOpenChange={(v) => { setAddDaily(v); if (!v) setEditingDaily(null); }} editing={editingDaily} onSaved={load} />
      <MarkReceivedDialog open={!!receiveTx} onOpenChange={(v) => !v && setReceiveTx(null)} transaction={receiveTx} onDone={load} />

      <AlertDialog open={!!confirmDaily} onOpenChange={(v) => !v && setConfirmDaily(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader><AlertDialogTitle>Supprimer cette saisie journalière ?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-daily" onClick={async () => {
              try {
                await api.delete(`/daily-revenues/${confirmDaily.id}`);
                toast.success("Saisie supprimée");
                setConfirmDaily(null); load();
              } catch (e) { toast.error(apiErr(e)); }
            }} className="rounded-full bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
